import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowLeft, Save, Plus, Trash2, Upload, FileText, ChevronRight, ChevronLeft, Eye, PenTool, Send, Users, FileCheck } from 'lucide-react'
import { contractsApi, contractTemplatesApi, clientsApi } from '@/lib/api'
import { getApiErrorMessage, isValidEmail, sanitizePhoneNumber } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RichTextEditor, RichTextEditorRef } from '@/components/ui/rich-text-editor'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { CreateContractRequest, UpdateContractRequest, SignerRequest, TemplateField, FieldValueRequest, SigningMode, SignaturePlacement, SignaturePlacementRequest } from '@/types'

interface FormData {
  title: string
  content: string
  template_id?: string
  client_id?: string
  contract_number?: string
  signing_mode: SigningMode
}

interface SignerFormData {
  name: string
  email: string
  signing_order: number
}

type WizardStep = 'details' | 'signers' | 'document' | 'review'

export default function ContractFormPage({ mode = 'create' }: { mode?: 'create' | 'edit' | 'view' }) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const isEdit = mode === 'edit' && !!id
  const isView = mode === 'view'

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('details')
  const [signers, setSigners] = useState<SignerFormData[]>([])
  const [newSigner, setNewSigner] = useState<{ name: string; email: string }>({ name: '', email: '' })
  const [templateFields, setTemplateFields] = useState<TemplateField[]>([])
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [showFieldsPanel, setShowFieldsPanel] = useState(true)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [editorContent, setEditorContent] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [documentSource, setDocumentSource] = useState<'template' | 'upload' | null>(null)
  const [signaturePlacements, setSignaturePlacements] = useState<SignaturePlacement[]>([])
  const [signaturePlacementMode, setSignaturePlacementMode] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editorRef = useRef<RichTextEditorRef>(null)
  const documentContainerRef = useRef<HTMLDivElement>(null)

  // Generate colors for signers based on index
  const getSignerColor = (index: number) => {
    const colors = [
      { bg: '#eff6ff', border: '#3b82f6', badge: '#3b82f6' }, // Blue
      { bg: '#ecfdf5', border: '#10b981', badge: '#10b981' }, // Green
      { bg: '#fffbeb', border: '#f59e0b', badge: '#f59e0b' }, // Amber
      { bg: '#f5f3ff', border: '#8b5cf6', badge: '#8b5cf6' }, // Purple
      { bg: '#fef2f2', border: '#ef4444', badge: '#ef4444' }, // Red
      { bg: '#f0fdf4', border: '#22c55e', badge: '#22c55e' }, // Emerald
    ]
    return colors[index % colors.length]
  }

  // Add a new signature placement with page-based coordinates
  const addSignaturePlacement = useCallback((signerIndex: number, pageNumber: number, xPercent: number, yPercent: number) => {
    const newPlacement: SignaturePlacement = {
      id: `sig-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      signerIndex,
      pageNumber,
      xPercent,
      yPercent,
    }
    setSignaturePlacements(prev => [...prev, newPlacement])
    setSignaturePlacementMode(null)
  }, [])

  // Delete a signature placement
  const deleteSignaturePlacement = useCallback((id: string) => {
    setSignaturePlacements(prev => prev.filter(p => p.id !== id))
  }, [])

  // Activate signature placement mode - user will click on document to place signature
  const activateSignaturePlacement = (signerIndex: number) => {
    setSignaturePlacementMode(signerIndex)
  }

  // State for dragging signature markers
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; startX: number; startY: number } | null>(null)

  // Handle click on document to place signature
  const handleDocumentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (signaturePlacementMode === null) return
    if (draggingId) return // Don't place while dragging

    // Don't place if clicking on an existing signature marker
    if ((e.target as HTMLElement).closest('[data-signature-marker]')) return

    const container = documentContainerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()

    // Calculate position as percentage of the A4 container
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100

    // Add signature at clicked position (page 1 for now - will be enhanced with PDF pages)
    addSignaturePlacement(signaturePlacementMode, 1, xPercent, yPercent)
  }, [signaturePlacementMode, addSignaturePlacement, draggingId])

  // Handle mouse down on signature marker to start dragging
  const handleMarkerMouseDown = useCallback((e: React.MouseEvent, placementId: string, currentX: number, currentY: number) => {
    e.preventDefault()
    e.stopPropagation()

    // Store the initial mouse position and marker position
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startX: currentX,
      startY: currentY
    }
    setDraggingId(placementId)
  }, [])

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggingId || !dragStartRef.current) return

    const container = documentContainerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()

    // Calculate delta in pixels
    const deltaX = e.clientX - dragStartRef.current.mouseX
    const deltaY = e.clientY - dragStartRef.current.mouseY

    // Convert delta to percentage of container dimensions
    const deltaXPercent = (deltaX / rect.width) * 100
    const deltaYPercent = (deltaY / rect.height) * 100

    // Calculate new position as percentages
    const newX = dragStartRef.current.startX + deltaXPercent
    const newY = dragStartRef.current.startY + deltaYPercent

    // Clamp values to keep marker within bounds
    const clampedX = Math.max(2, Math.min(98, newX))
    const clampedY = Math.max(2, Math.min(98, newY))

    // Update the placement position
    setSignaturePlacements(prev => prev.map(p =>
      p.id === draggingId
        ? { ...p, xPercent: clampedX, yPercent: clampedY }
        : p
    ))
  }, [draggingId])

  // Handle mouse up to stop dragging
  const handleMouseUp = useCallback(() => {
    setDraggingId(null)
    dragStartRef.current = null
  }, [])

  // Handle escape key to cancel placement mode
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (signaturePlacementMode !== null) {
          setSignaturePlacementMode(null)
        }
        if (draggingId !== null) {
          setDraggingId(null)
        }
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [signaturePlacementMode, draggingId])

  // Add global mouse up listener for drag end
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (draggingId) {
        setDraggingId(null)
      }
    }
    window.addEventListener('mouseup', handleGlobalMouseUp)
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp)
  }, [draggingId])



  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      title: '',
      content: '',
      signing_mode: 'PARALLEL',
    },
  })

  const signingMode = watch('signing_mode')

  const { data: contract, isLoading: contractLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsApi.getById(id!),
    enabled: !!id,
  })

  const { data: templates = [] } = useQuery({
    queryKey: ['contractTemplates', 'active'],
    queryFn: () => contractTemplatesApi.getAll(true),
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getAllLegacy(),
  })

  useEffect(() => {
    if (contract) {
      reset({
        title: contract.title,
        content: contract.content,
        template_id: contract.template_id || undefined,
        client_id: contract.client_id || undefined,
        contract_number: contract.contract_number,
        signing_mode: contract.signing_mode || 'PARALLEL',
      })
      setSigners(contract.signers.map(s => ({
        name: s.name,
        email: s.email,
        signing_order: s.signing_order,
      })))
      setEditorContent(contract.content)
    }
  }, [contract, reset])

  const selectedTemplateId = watch('template_id')
  useEffect(() => {
    if (selectedTemplateId && !isEdit) {
      const template = templates.find(t => t.template_id === selectedTemplateId)
      if (template) {
        contractTemplatesApi.getById(selectedTemplateId).then(fullTemplate => {
          setValue('content', fullTemplate.content)
          if (!watch('title')) {
            setValue('title', fullTemplate.template_name)
          }
          // Load template fields and initialize field values
          if (fullTemplate.fields && fullTemplate.fields.length > 0) {
            setTemplateFields(fullTemplate.fields)
            const initialValues: Record<string, string> = {}
            fullTemplate.fields.forEach(field => {
              initialValues[field.field_name] = field.default_value || ''
            })
            setFieldValues(initialValues)
          } else {
            setTemplateFields([])
            setFieldValues({})
          }
        })
      }
    } else if (!selectedTemplateId && !isEdit) {
      // Clear fields when no template is selected
      setTemplateFields([])
      setFieldValues({})
    }
  }, [selectedTemplateId, templates, setValue, isEdit, watch])

  const createMutation = useMutation({
    mutationFn: (data: CreateContractRequest) => contractsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast.success('Contract created successfully')
      navigate('/contracts')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to create contract'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateContractRequest) => contractsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      queryClient.invalidateQueries({ queryKey: ['contract', id] })
      toast.success('Contract updated successfully')
      navigate('/contracts')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to update contract'))
    },
  })

  const addSigner = () => {
    if (newSigner.name && newSigner.email) {
      // Validate email format
      if (!isValidEmail(newSigner.email)) {
        toast.error('Please enter a valid email address for the signer')
        return
      }
      const newSignerData: SignerFormData = {
        name: newSigner.name,
        email: newSigner.email,
        signing_order: signers.length + 1,
      }
      setSigners([...signers, newSignerData])
      setNewSigner({ name: '', email: '' })
    }
  }

  const removeSigner = (index: number) => {
    const updatedSigners = signers.filter((_, i) => i !== index)
    // Re-order remaining signers
    setSigners(updatedSigners.map((s, i) => ({ ...s, signing_order: i + 1 })))
  }

  const moveSignerUp = (index: number) => {
    if (index === 0) return
    const newSigners = [...signers]
    const temp = newSigners[index]
    newSigners[index] = newSigners[index - 1]
    newSigners[index - 1] = temp
    setSigners(newSigners.map((s, i) => ({ ...s, signing_order: i + 1 })))
  }

  const moveSignerDown = (index: number) => {
    if (index === signers.length - 1) return
    const newSigners = [...signers]
    const temp = newSigners[index]
    newSigners[index] = newSigners[index + 1]
    newSigners[index + 1] = temp
    setSigners(newSigners.map((s, i) => ({ ...s, signing_order: i + 1 })))
  }

  // Wizard navigation
  const steps: WizardStep[] = ['details', 'signers', 'document', 'review']
  const currentStepIndex = steps.indexOf(currentStep)

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 'details':
        return !!watch('title')
      case 'signers':
        return signers.length > 0
      case 'document':
        return !!editorContent
      default:
        return true
    }
  }

  const goToNextStep = () => {
    const nextIndex = currentStepIndex + 1
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex])
    }
  }

  const goToPreviousStep = () => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex])
    }
  }

  // Handle Word document upload
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ]
    if (!validTypes.includes(file.type) && !file.name.endsWith('.docx') && !file.name.endsWith('.doc')) {
      toast.error('Please upload a Word document (.doc or .docx)')
      return
    }

    setUploadingDoc(true)
    try {
      // Dynamically import mammoth for Word parsing
      const mammoth = await import('mammoth')
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.convertToHtml({ arrayBuffer })

      if (result.value) {
        setEditorContent(result.value)
        setValue('content', result.value)
        toast.success('Document imported successfully')
      }
    } catch {
      toast.error('Failed to parse document. Please try a different file.')
    } finally {
      setUploadingDoc(false)
      // Reset input so same file can be selected again
      e.target.value = ''
    }
  }

  // Sync editor content with form
  const handleEditorChange = (html: string) => {
    setEditorContent(html)
    setValue('content', html)
  }

  // Generate preview content with field values replaced
  const previewContent = useMemo(() => {
    let content = editorContent || watch('content') || ''

    // Replace placeholders like {{field_name}} with actual values
    Object.entries(fieldValues).forEach(([fieldName, fieldValue]) => {
      const placeholder = new RegExp(`{{\\s*${fieldName}\\s*}}`, 'gi')
      content = content.replace(placeholder, fieldValue || `<span style="background-color: #fef3c7; padding: 2px 4px; border-radius: 2px;">{{${fieldName}}}</span>`)
    })

    return content
  }, [editorContent, fieldValues, watch])

  // Initialize editor content from form
  useEffect(() => {
    const content = watch('content')
    if (content && content !== editorContent) {
      setEditorContent(content)
    }
  }, [watch('content')])

  // Auto-enable preview when user starts filling fields
  useEffect(() => {
    if (!isView && templateFields.length > 0) {
      const hasFilledFields = Object.values(fieldValues).some(value => value && value.trim() !== '')
      if (hasFilledFields && !showPreview) {
        setShowPreview(true)
      }
    }
  }, [fieldValues, templateFields.length, isView])

  // Helper function to replace placeholders with actual values
  const replaceContentPlaceholders = (content: string): string => {
    let processedContent = content

    // Replace placeholders like {{field_name}} with actual values
    Object.entries(fieldValues).forEach(([fieldName, fieldValue]) => {
      const placeholder = new RegExp(`{{\\s*${fieldName}\\s*}}`, 'gi')
      processedContent = processedContent.replace(placeholder, fieldValue || '')
    })

    return processedContent
  }

  // Convert placements to API format (PDF page-based)
  const convertPlacementsForApi = (): SignaturePlacementRequest[] => {
    if (signaturePlacements.length === 0) return []

    return signaturePlacements.map(placement => ({
      signer_index: placement.signerIndex,
      page_number: placement.pageNumber,
      x_percent: placement.xPercent,
      y_percent: placement.yPercent,
    }))
  }

  const onSubmit = (data: FormData) => {
    const signerRequests: SignerRequest[] = signers.map((s) => ({
      name: s.name,
      email: s.email,
      signing_order: s.signing_order,
    }))

    // Build field values array from the fieldValues state - only include fields with valid field_id
    const fieldValueRequests: FieldValueRequest[] = templateFields
      .filter(field => field.field_id) // Only include fields that have a field_id
      .map(field => ({
        field_id: field.field_id!,
        field_name: field.field_name,
        field_value: fieldValues[field.field_name] || '',
      }))

    // Replace placeholders with actual values before saving
    const finalContent = replaceContentPlaceholders(data.content)

    // Convert signature placements to API format (marker-based)
    const signaturePlacementRequests = convertPlacementsForApi()

    if (isEdit) {
      updateMutation.mutate({
        title: data.title,
        content: finalContent,
      })
    } else {
      // Clean up the data - convert empty strings to undefined for optional fields
      const createRequest: CreateContractRequest = {
        title: data.title,
        content: finalContent,
        signing_mode: data.signing_mode,
        currency: 'USD', // Default currency since it's required by backend
        template_id: data.template_id || undefined,
        client_id: data.client_id || undefined,
        contract_number: data.contract_number || undefined,
        signers: signerRequests.length > 0 ? signerRequests : undefined,
        field_values: fieldValueRequests.length > 0 ? fieldValueRequests : undefined,
        signature_placements: signaturePlacementRequests.length > 0 ? signaturePlacementRequests : undefined,
      }
      createMutation.mutate(createRequest)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  if (id && contractLoading) {
    return <div className="text-center py-8">Loading...</div>
  }

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <div key={step} className="flex items-center">
          <button
            type="button"
            onClick={() => {
              // Only allow going back or to completed steps
              if (index <= currentStepIndex) {
                setCurrentStep(step)
              }
            }}
            className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
              index < currentStepIndex
                ? 'bg-primary border-primary text-primary-foreground'
                : index === currentStepIndex
                ? 'border-primary text-primary bg-primary/10'
                : 'border-muted-foreground/30 text-muted-foreground'
            }`}
          >
            {index < currentStepIndex ? (
              <FileCheck className="h-5 w-5" />
            ) : (
              <span className="font-semibold">{index + 1}</span>
            )}
          </button>
          <span className={`ml-2 text-sm font-medium ${
            index === currentStepIndex ? 'text-primary' : 'text-muted-foreground'
          }`}>
            {step === 'details' && 'Contract Details'}
            {step === 'signers' && 'Signers'}
            {step === 'document' && 'Document'}
            {step === 'review' && 'Review & Send'}
          </span>
          {index < steps.length - 1 && (
            <div className={`w-16 h-0.5 mx-4 ${
              index < currentStepIndex ? 'bg-primary' : 'bg-muted-foreground/30'
            }`} />
          )}
        </div>
      ))}
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isView ? 'View Contract' : isEdit ? 'Edit Contract' : 'Create Contract'}
          </h1>
          <p className="text-muted-foreground">
            {isView ? 'Contract details' : isEdit ? 'Update contract details' : 'Follow the steps to create a new contract'}
          </p>
        </div>
      </div>

      {!isView && !isEdit && <StepIndicator />}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Contract Details */}
        {(currentStep === 'details' || isView || isEdit) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Contract Details
              </CardTitle>
              <CardDescription>Basic information about the contract</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="title">Contract Name *</Label>
                  <Input
                    id="title"
                    {...register('title', { required: 'Contract name is required' })}
                    disabled={isView}
                    placeholder="e.g., Service Agreement - Acme Corp"
                  />
                  {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contract_number">Contract Number</Label>
                  <Input
                    id="contract_number"
                    {...register('contract_number')}
                    disabled={isView || isEdit}
                    placeholder="Auto-generated if left empty"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_id">Client (Optional)</Label>
                <Select
                  value={watch('client_id') || ''}
                  onValueChange={(v) => setValue('client_id', v || undefined)}
                  disabled={isView}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c: any) => (
                      <SelectItem key={c.client_id} value={c.client_id}>
                        {c.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Document Source Selection */}
              {!isView && !isEdit && (
                <div className="space-y-4 pt-4 border-t">
                  <Label>Document Source</Label>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card
                      className={`cursor-pointer transition-all ${documentSource === 'template' ? 'ring-2 ring-primary' : 'hover:border-primary/50'}`}
                      onClick={() => setDocumentSource('template')}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <p className="font-medium">Use Template</p>
                          <p className="text-sm text-muted-foreground">Select from existing templates</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card
                      className={`cursor-pointer transition-all ${documentSource === 'upload' ? 'ring-2 ring-primary' : 'hover:border-primary/50'}`}
                      onClick={() => setDocumentSource('upload')}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <Upload className="h-8 w-8 text-primary" />
                        <div>
                          <p className="font-medium">Upload Document</p>
                          <p className="text-sm text-muted-foreground">Upload a Word document</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {documentSource === 'template' && (
                    <div className="space-y-2">
                      <Label>Select Template</Label>
                      <Select
                        value={watch('template_id') || ''}
                        onValueChange={(v) => {
                          setValue('template_id', v || undefined)
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => (
                            <SelectItem key={t.template_id} value={t.template_id}>
                              {t.template_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {documentSource === 'upload' && (
                    <div className="space-y-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleDocumentUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingDoc}
                        className="w-full"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingDoc ? 'Importing...' : 'Choose Word Document'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>

            {/* Navigation buttons for Step 1 */}
            {!isView && !isEdit && currentStep === 'details' && (
              <div className="flex justify-end p-6 pt-0">
                <Button
                  type="button"
                  onClick={goToNextStep}
                  disabled={!canProceedToNextStep()}
                >
                  Next: Add Signers
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Step 2: Signers */}
        {(currentStep === 'signers' || isView || isEdit) && (
          <Card className={isView || isEdit ? 'mt-6' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Signers
              </CardTitle>
              <CardDescription>Add people who need to sign this contract</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Signing Mode Selection */}
              {!isView && (
                <div className="space-y-3">
                  <Label>Signing Order</Label>
                  <RadioGroup
                    value={signingMode}
                    onValueChange={(v: string) => setValue('signing_mode', v as SigningMode)}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="PARALLEL" id="parallel" />
                      <Label htmlFor="parallel" className="font-normal cursor-pointer">
                        <span className="font-medium">Parallel</span>
                        <span className="text-muted-foreground ml-1">- All signers receive the contract at once</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="SEQUENTIAL" id="sequential" />
                      <Label htmlFor="sequential" className="font-normal cursor-pointer">
                        <span className="font-medium">Sequential</span>
                        <span className="text-muted-foreground ml-1">- Signers receive in order</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Signers List */}
              <div className="space-y-3">
                {signers.map((signer, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 border rounded-lg" style={{ borderLeftColor: getSignerColor(index).border, borderLeftWidth: '4px' }}>
                    {signingMode === 'SEQUENTIAL' && (
                      <div className="flex flex-col gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveSignerUp(index)}
                          disabled={index === 0 || isView}
                        >
                          <ChevronLeft className="h-4 w-4 rotate-90" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveSignerDown(index)}
                          disabled={index === signers.length - 1 || isView}
                        >
                          <ChevronRight className="h-4 w-4 rotate-90" />
                        </Button>
                      </div>
                    )}
                    {signingMode === 'SEQUENTIAL' && (
                      <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center">
                        {index + 1}
                      </Badge>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{signer.name}</p>
                      <p className="text-sm text-muted-foreground">{signer.email}</p>
                    </div>
                    {!isView && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeSigner(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Signer Form */}
              {!isView && (
                <div className="flex gap-4 items-end p-4 border rounded-lg bg-muted/30">
                  <div className="flex-1 space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={newSigner.name}
                      onChange={(e) => setNewSigner({ ...newSigner, name: e.target.value })}
                      placeholder="Signer's full name"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newSigner.email}
                      onChange={(e) => setNewSigner({ ...newSigner, email: e.target.value })}
                      placeholder="signer@example.com"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={addSigner}
                    disabled={!newSigner.name || !newSigner.email}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Signer
                  </Button>
                </div>
              )}
            </CardContent>

            {/* Navigation buttons for Step 2 */}
            {!isView && !isEdit && currentStep === 'signers' && (
              <div className="flex justify-between p-6 pt-0">
                <Button type="button" variant="outline" onClick={goToPreviousStep}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={goToNextStep}
                  disabled={!canProceedToNextStep()}
                >
                  Next: Document
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Step 3: Document */}
        {(currentStep === 'document' || isView || isEdit) && (
          <div className={isView || isEdit ? 'mt-6' : ''}>
            <div className="flex gap-4">
              {/* Main Document Editor */}
              <div className={`flex-1 transition-all duration-300 ${showFieldsPanel && templateFields.length > 0 ? 'pr-0' : ''}`}>
                <Card className="h-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Contract Document
                        </CardTitle>
                        <CardDescription>
                          {showPreview ? 'Preview with filled values' : 'Edit the contract document'}
                        </CardDescription>
                      </div>
                      {!isView && (
                        <div className="flex items-center gap-2">
                          {templateFields.length > 0 && (
                            <Button
                              type="button"
                              variant={showPreview ? "default" : "outline"}
                              size="sm"
                              onClick={() => setShowPreview(!showPreview)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {showPreview ? 'Edit' : 'Preview'}
                            </Button>
                          )}
                          {signers.length > 0 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant={signaturePlacementMode !== null ? "default" : "outline"}
                                  size="sm"
                                >
                                  <PenTool className="h-4 w-4 mr-2" />
                                  {signaturePlacementMode !== null ? 'Placing...' : 'Add Signature'}
                                  {signaturePlacements.length > 0 && (
                                    <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                                      {signaturePlacements.length}
                                    </Badge>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {signers.map((signer, index) => (
                                  <DropdownMenuItem key={index} onClick={() => activateSignaturePlacement(index)}>
                                    <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: getSignerColor(index).badge }} />
                                    {signer.name}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Signature Placement Mode Indicator */}
                    {signaturePlacementMode !== null && !isView && (
                      <div className="mb-3 p-3 bg-primary/10 border border-primary rounded-md flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <PenTool className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium text-primary">
                            Click anywhere in the document below to place signature for {signers[signaturePlacementMode]?.name}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSignaturePlacementMode(null)}
                        >
                          Cancel (Esc)
                        </Button>
                      </div>
                    )}

                    {/* Signature Placement Info */}
                    {signaturePlacements.length === 0 && signers.length > 0 && signaturePlacementMode === null && !isView && (
                      <div className="mb-3 p-3 bg-muted/50 border rounded-md">
                        <p className="text-sm text-muted-foreground">
                          Use the "Add Signature" button above, then click on the document where you want to place each signer's signature.
                        </p>
                      </div>
                    )}

                    {/* Document Editor - shown when not in preview mode, not in view mode, and not placing signatures */}
                    {!showPreview && !isView && signaturePlacementMode === null && signaturePlacements.length === 0 && (
                      <div className="mb-4">
                        <RichTextEditor
                          ref={editorRef}
                          content={editorContent}
                          onChange={handleEditorChange}
                          placeholder="Start typing your contract content or upload a Word document..."
                          minHeight="300px"
                          maxHeight="350px"
                        />
                      </div>
                    )}

                    {/* Document Preview - shown in preview mode, view mode, placement mode, or when signatures are placed */}
                    {(showPreview || isView || signaturePlacementMode !== null || signaturePlacements.length > 0) && (
                      <div className="space-y-2">
                        {/* A4 Document Container - fixed aspect ratio */}
                        <div className="max-h-[600px] overflow-y-auto border rounded-md bg-muted/30 p-4">
                          <div
                            ref={documentContainerRef}
                            onClick={handleDocumentClick}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            className={`relative bg-white shadow-md mx-auto select-none ${
                              signaturePlacementMode !== null ? 'cursor-crosshair ring-2 ring-primary' : ''
                            } ${draggingId ? 'cursor-grabbing' : ''}`}
                            style={{
                              width: '210mm',
                              minHeight: '297mm',
                              padding: '20mm',
                              boxSizing: 'border-box',
                            }}
                          >
                            {/* Document Content */}
                            <div
                              className="contract-content prose prose-sm max-w-none pointer-events-none"
                              dangerouslySetInnerHTML={{ __html: previewContent }}
                            />

                            {/* Signature Placement Markers */}
                            {signaturePlacements.map((placement) => {
                              const signer = signers[placement.signerIndex]
                              const colors = getSignerColor(placement.signerIndex)
                              const isDragging = draggingId === placement.id
                              return (
                                <div
                                  key={placement.id}
                                  data-signature-marker="true"
                                  onMouseDown={(e) => !isView && handleMarkerMouseDown(e, placement.id, placement.xPercent, placement.yPercent)}
                                  className={`absolute flex items-center gap-1 px-2 py-1 rounded border-2 text-xs font-medium shadow-sm ${
                                    !isView ? 'cursor-grab hover:shadow-md active:cursor-grabbing' : ''
                                  } ${isDragging ? 'cursor-grabbing shadow-lg z-50' : ''}`}
                                  style={{
                                    left: `${placement.xPercent}%`,
                                    top: `${placement.yPercent}%`,
                                    transform: 'translate(-50%, -50%)',
                                    borderColor: colors.border,
                                    backgroundColor: colors.bg,
                                    color: colors.badge,
                                    zIndex: isDragging ? 100 : 10,
                                  }}
                                >
                                  <PenTool className="h-3 w-3" />
                                  <span>{signer?.name || 'Unknown'}</span>
                                  {!isView && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        deleteSignaturePlacement(placement.id)
                                      }}
                                      className="ml-1 hover:text-destructive"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>

                        {/* Signature Placements Summary */}
                        {signaturePlacements.length > 0 && !isView && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-md">
                            <Label className="text-sm text-muted-foreground mb-2 block">
                              Signature Placements ({signaturePlacements.length})
                            </Label>
                            <div className="flex flex-wrap gap-2">
                              {signaturePlacements.map((placement) => {
                                const signer = signers[placement.signerIndex]
                                const colors = getSignerColor(placement.signerIndex)
                                return (
                                  <div
                                    key={placement.id}
                                    className="flex items-center gap-2 px-2 py-1 rounded border text-sm"
                                    style={{ borderColor: colors.border, backgroundColor: colors.bg }}
                                  >
                                    <span style={{ color: colors.badge }}>✍ {signer?.name || 'Unknown'} (Page {placement.pageNumber})</span>
                                    <button
                                      type="button"
                                      onClick={() => deleteSignaturePlacement(placement.id)}
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      ×
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {errors.content && <p className="text-sm text-destructive mt-2">{errors.content.message}</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Form Fields Side Panel */}
              {templateFields.length > 0 && (
                <>
                  {/* Toggle Button */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto py-4 px-1 self-start mt-16"
                    onClick={() => setShowFieldsPanel(!showFieldsPanel)}
                  >
                    {showFieldsPanel ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                  </Button>

                  {/* Fields Panel */}
                  <div className={`transition-all duration-300 overflow-hidden ${showFieldsPanel ? 'w-80' : 'w-0'}`}>
                    {showFieldsPanel && (
                      <Card className="h-full">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base">Form Fields</CardTitle>
                            <Badge variant="secondary">{templateFields.length}</Badge>
                          </div>
                          <CardDescription className="text-xs">Fill in the required information</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                          <ScrollArea className="h-[550px] px-4 pb-4">
                            <div className="space-y-4">
                              {templateFields.map((field) => (
                                <div key={field.field_name} className="space-y-1.5">
                                  <Label htmlFor={`panel-${field.field_name}`} className="text-sm">
                                    {field.field_label}
                                    {field.is_required && <span className="text-destructive ml-1">*</span>}
                                  </Label>
                                  {field.field_type === 'TEXT' && (
                                    <Input
                                      id={`panel-${field.field_name}`}
                                      value={fieldValues[field.field_name] || ''}
                                      onChange={(e) => setFieldValues({ ...fieldValues, [field.field_name]: e.target.value })}
                                      placeholder={field.placeholder}
                                      disabled={isView}
                                      className="h-8 text-sm"
                                    />
                                  )}
                                  {field.field_type === 'TEXTAREA' && (
                                    <Textarea
                                      id={`panel-${field.field_name}`}
                                      value={fieldValues[field.field_name] || ''}
                                      onChange={(e) => setFieldValues({ ...fieldValues, [field.field_name]: e.target.value })}
                                      placeholder={field.placeholder}
                                      disabled={isView}
                                      rows={3}
                                      className="text-sm"
                                    />
                                  )}
                                  {field.field_type === 'NUMBER' && (
                                    <Input
                                      id={`panel-${field.field_name}`}
                                      type="number"
                                      value={fieldValues[field.field_name] || ''}
                                      onChange={(e) => setFieldValues({ ...fieldValues, [field.field_name]: e.target.value })}
                                      placeholder={field.placeholder}
                                      disabled={isView}
                                      className="h-8 text-sm"
                                    />
                                  )}
                                  {field.field_type === 'DATE' && (
                                    <Input
                                      id={`panel-${field.field_name}`}
                                      type="date"
                                      value={fieldValues[field.field_name] || ''}
                                      onChange={(e) => setFieldValues({ ...fieldValues, [field.field_name]: e.target.value })}
                                      disabled={isView}
                                      className="h-8 text-sm"
                                    />
                                  )}
                                  {field.field_type === 'EMAIL' && (
                                    <Input
                                      id={`panel-${field.field_name}`}
                                      type="email"
                                      value={fieldValues[field.field_name] || ''}
                                      onChange={(e) => setFieldValues({ ...fieldValues, [field.field_name]: e.target.value })}
                                      placeholder={field.placeholder}
                                      disabled={isView}
                                      className="h-8 text-sm"
                                    />
                                  )}
                                  {field.field_type === 'PHONE' && (
                                    <Input
                                      id={`panel-${field.field_name}`}
                                      type="tel"
                                      value={fieldValues[field.field_name] || ''}
                                      onChange={(e) => setFieldValues({ ...fieldValues, [field.field_name]: sanitizePhoneNumber(e.target.value) })}
                                      placeholder={field.placeholder}
                                      disabled={isView}
                                      className="h-8 text-sm"
                                    />
                                  )}
                                  {field.field_type === 'CHECKBOX' && (
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        id={`panel-${field.field_name}`}
                                        checked={fieldValues[field.field_name] === 'true'}
                                        onCheckedChange={(checked) => setFieldValues({ ...fieldValues, [field.field_name]: checked ? 'true' : 'false' })}
                                        disabled={isView}
                                      />
                                      <Label htmlFor={`panel-${field.field_name}`} className="font-normal text-sm">{field.placeholder || 'Yes'}</Label>
                                    </div>
                                  )}
                                  {(field.field_type === 'DROPDOWN' || field.field_type === 'RADIO') && field.options && (
                                    <Select
                                      value={fieldValues[field.field_name] || ''}
                                      onValueChange={(v) => setFieldValues({ ...fieldValues, [field.field_name]: v })}
                                      disabled={isView}
                                    >
                                      <SelectTrigger className="h-8 text-sm">
                                        <SelectValue placeholder={field.placeholder || 'Select...'} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {field.options.map((opt) => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Navigation buttons for Step 3 */}
            {!isView && !isEdit && currentStep === 'document' && (
              <div className="flex justify-between mt-6">
                <Button type="button" variant="outline" onClick={goToPreviousStep}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={goToNextStep}
                  disabled={!canProceedToNextStep()}
                >
                  Next: Review
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Review & Send */}
        {currentStep === 'review' && !isView && !isEdit && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Review & Send
              </CardTitle>
              <CardDescription>Review your contract before sending for signatures</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Contract Name</Label>
                  <p className="font-medium">{watch('title')}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Signing Mode</Label>
                  <p className="font-medium">{signingMode === 'PARALLEL' ? 'Parallel (All at once)' : 'Sequential (In order)'}</p>
                </div>
              </div>

              {/* Signers Summary */}
              <div className="space-y-3">
                <Label className="text-muted-foreground">Signers ({signers.length})</Label>
                <div className="grid gap-2">
                  {signers.map((signer, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg" style={{ borderLeftColor: getSignerColor(index).border, borderLeftWidth: '4px' }}>
                      {signingMode === 'SEQUENTIAL' && (
                        <Badge variant="outline" className="w-6 h-6 rounded-full flex items-center justify-center text-xs">
                          {index + 1}
                        </Badge>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-sm">{signer.name}</p>
                        <p className="text-xs text-muted-foreground">{signer.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Document Preview with signature markers - A4 format */}
              <div className="space-y-3">
                <Label className="text-muted-foreground">Document Preview</Label>
                <div className="max-h-[500px] overflow-y-auto border rounded-md bg-muted/30 p-4">
                  <div
                    className="relative bg-white shadow-md mx-auto"
                    style={{
                      width: '210mm',
                      minHeight: '297mm',
                      padding: '20mm',
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* Document Content */}
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewContent }} />

                    {/* Signature Placement Markers */}
                    {signaturePlacements.map((placement) => {
                      const signer = signers[placement.signerIndex]
                      const colors = getSignerColor(placement.signerIndex)
                      return (
                        <div
                          key={placement.id}
                          className="absolute flex items-center gap-1 px-2 py-1 rounded border-2 text-xs font-medium shadow-sm"
                          style={{
                            left: `${placement.xPercent}%`,
                            top: `${placement.yPercent}%`,
                            transform: 'translate(-50%, -50%)',
                            borderColor: colors.border,
                            backgroundColor: colors.bg,
                            color: colors.badge,
                            zIndex: 10,
                          }}
                        >
                          <PenTool className="h-3 w-3" />
                          <span>{signer?.name || 'Unknown'}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </CardContent>

            {/* Final Actions */}
            <div className="flex justify-between p-6 pt-0">
              <Button type="button" variant="outline" onClick={goToPreviousStep}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => navigate('/contracts')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  <Save className="mr-2 h-4 w-4" />
                  {isSubmitting ? 'Creating...' : 'Create Contract'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Edit/View mode buttons */}
        {(isView || isEdit) && (
          <div className="flex justify-end gap-4 mt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/contracts')}>
              {isView ? 'Back' : 'Cancel'}
            </Button>
            {isEdit && (
              <Button type="submit" disabled={isSubmitting}>
                <Save className="mr-2 h-4 w-4" />
                {isSubmitting ? 'Saving...' : 'Update Contract'}
              </Button>
            )}
          </div>
        )}
      </form>
    </div>
  )
}

